import './AreaIndicatorMoveModal.css';

import React, { useMemo, useState, useEffect } from 'react';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import AreaSelector from '../components/areas/AreaSelector';
import AreaIndicatorUtils from '../components/area-indicators/AreaIndicatorUtils';
import useAreasNotCoveringDateRange from '../hooks/useAreasNotCoveringDateRange';
import useMoveAreaIndicator from '../hooks/useMoveAreaIndicator';
import B2BStepper from '../../b2b-common/B2BStepper';
import { useContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import { useNavigate } from 'react-router-dom';

const AreaIndicatorMoveModal = ({ open, areaIndicator, onClose = null, onRefresh = null }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const { setNotification } = useContentContext();
    const navigate = useNavigate();
    const [targetAreaSelection, setTargetAreaSelection] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    
    const { 
        moveAreaIndicator, 
        loading,
        resetState 
    } = useMoveAreaIndicator();

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            resetState();
            setTargetAreaSelection([]);
            setCurrentStep(0);
        }
    }, [open, resetState]);

    const handleTargetAreaSelected = (selectedPlanAreaDTOs) => {
        setTargetAreaSelection(selectedPlanAreaDTOs);
        setCurrentStep(1); // Move to the next step when an area is selected
    }

    // Get the date range from objectives
    const dateRange = useMemo(() => {
        return AreaIndicatorUtils.getObjectivesDateRange(areaIndicator);
    }, [areaIndicator]);

    // Get areas that don't cover the date range
    const { areaIds: uneligibleAreaList } = useAreasNotCoveringDateRange(
        dateRange?.minDate,
        dateRange?.maxDate
    );

    // Add the current area ID to the uneligible list (can't move to the same area)
    const finalUneligibleAreaList = useMemo(() => {
        const currentAreaId = areaIndicator?.area?.areaId;
        return currentAreaId ? [...(uneligibleAreaList || []), currentAreaId] : uneligibleAreaList;
    }, [uneligibleAreaList, areaIndicator?.area?.areaId]);

    const handleMoveAreaIndicator = async () => {
        const source_planUUID = areaIndicator.area.plan.planUUID;
        const target_planUUID = targetAreaSelection[0]?.planUUID;
        const source_areaId = areaIndicator.area.areaId;
        const target_areaId = targetAreaSelection[0]?.planAreaId;
        
        const result = await moveAreaIndicator(
            source_planUUID, 
            source_areaId, 
            areaIndicator.areaIndicatorId,
            target_planUUID, 
            target_areaId
        );
        
        if (result.success) {
            showNotification(
                setNotification, 
                t('Area indicator moved successfully'), 
                'success', 
                3000
            );
            if (onRefresh) {
                onRefresh();
            }
            if (onClose) {
                onClose();
            }
            // Navigate to the target area indicator
            navigate(`/plans/plans-explorer/${target_planUUID}?selectedAreaId=${encodeURIComponent(target_areaId)}&selectedAreaIndicatorId=${encodeURIComponent(result.result.areaIndicatorId)}`);
        } else {
            showNotification(
                setNotification, 
                t('Error moving area indicator'), 
                'error', 
                5000
            );
        }
    }

    const steps_to_move_area_indicator = [
        {
            label: t('Select destination area'),
            description: (<p>{t('Navigate through the plans and select the area where you wish to move to the area indicator.')}</p>),
            content: (
                <AreaSelector
                    selectedPlanAreaDTOs={targetAreaSelection}
                    onSelectedPlanAreaDTOsUpdate={handleTargetAreaSelected}
                    multiselect={false} // Only one area can be selected for moving
                    className='area-indicator-move-modal-area-selector'
                    uneligibleAreaList={finalUneligibleAreaList}
                />                
            ),
            disableNext: targetAreaSelection.length === 0
        },
        {
            label: t('Review and confirm'),
            description: (<p>{t('If you confirm the selected area indicator will be moved to the destination area.')}</p>),
            content: (null),
            disableNext: false
        }
    ];

    return (
        <B2BPopUpForm
            open={open}
            title={<span>{t('Move Area Indicator')} [{areaIndicator.areaIndicatorName}]</span>}
            onClose={onClose}
            className={'area-indicator-move-modal b2b-light-01'}
        >
            {loading ? (
                <div className="loading-spinner-container">
                    <div className="loading-spinner"></div>
                    <p>{t('Moving area indicator...')}</p>
                </div>
            ) : (
                <B2BStepper
                    steps={steps_to_move_area_indicator}
                    className={'area-indicator-move-modal-stepper'}
                    onSuccess={handleMoveAreaIndicator}
                    onCancel={onClose}
                    key={targetAreaSelection.length > 0 ? targetAreaSelection[0] : 'default'}
                    currentStep={currentStep}
                />
            )}
        </B2BPopUpForm>
    );
}

export default AreaIndicatorMoveModal;
